import { Controller } from "@hotwired/stimulus"

// Tab controller with WAI-ARIA support and cancelable tab switching
// Uses Stimulus CSS classes API for styling flexibility
export default class extends Controller {
  static targets = ["tab", "panel", "tooltip", "indicator", "panelsContainer"]
  static classes = ["activeTab", "inactiveTab", "hiddenPanel"]
  static values = {
    index: { type: Number, default: 0 },
    storageKey: { type: String, default: "" }
  }

  connect() {
    // Read saved tab index from localStorage if storageKey is set
    const savedIndex = this.loadSavedIndex()
    const initialIndex = savedIndex !== null ? savedIndex : this.indexValue

    // Update indexValue to match the restored/initial tab
    this.indexValue = initialIndex
    this.showTab(initialIndex, false, false) // Don't dispatch events or animate on initial load
    this.setupAriaAttributes()

    // Announce the initial/restored tab. Restoring must not fire tabs:switched
    // (no animations or switch side effects on load), but listeners that mirror
    // the active tab into form state (e.g. upload-form's hidden upload_type
    // field) still need to know which tab is active. Outlet-connected callbacks
    // are not a substitute: they can fire before this connect() restores the
    // saved index and would read the stale default.
    this.dispatch("restored", { detail: { index: initialIndex } })

    // Position indicator after layout is ready
    if (this.hasIndicatorTarget) {
      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        this.positionIndicator(initialIndex, false)
      })
    }
  }

  // Click handler for tab selection
  select(event) {
    const clickedTab = event.currentTarget
    const index = this.tabTargets.indexOf(clickedTab)

    if (index === this.indexValue) return // Already on this tab

    // Dispatch cancelable before-switch event
    const beforeSwitchEvent = this.dispatch("before-switch", {
      detail: { fromIndex: this.indexValue, toIndex: index },
      cancelable: true
    })

    if (beforeSwitchEvent.defaultPrevented) {
      // Tab switch was blocked
      return
    }

    this.switchToTab(index)
  }

  // Keyboard navigation (WAI-ARIA tabs pattern). Disabled tabs are skipped:
  // a disabled button cannot take focus, so stopping on one strands the
  // keyboard (the offline copy disables the File tab).
  keydown(event) {
    let newIndex

    switch (event.key) {
      case "ArrowLeft":
        newIndex = this.nextEnabledIndex(this.focusedIndex(), -1)
        break
      case "ArrowRight":
        newIndex = this.nextEnabledIndex(this.focusedIndex(), 1)
        break
      case "Home":
        newIndex = this.nextEnabledIndex(-1, 1)
        break
      case "End":
        newIndex = this.nextEnabledIndex(this.tabTargets.length, -1)
        break
      case "Enter":
      case " ":
        // Activate the focused tab
        event.preventDefault()
        this.activateFocusedTab()
        return
      default:
        return // Let other keys through
    }

    event.preventDefault()

    // Move focus to new tab but don't activate it yet
    if (newIndex !== null) this.tabTargets[newIndex].focus()
  }

  // Arrows move from the tab that has focus, which is not yet the active one
  // after an earlier arrow press.
  focusedIndex() {
    const index = this.tabTargets.indexOf(document.activeElement)
    return index === -1 ? this.indexValue : index
  }

  // The first enabled tab after `from` in the `step` direction, wrapping around.
  nextEnabledIndex(from, step) {
    const count = this.tabTargets.length
    for (let offset = 1; offset <= count; offset++) {
      const index = (((from + step * offset) % count) + count) % count
      if (!this.isDisabled(this.tabTargets[index])) return index
    }
    return null
  }

  isDisabled(tab) {
    return tab.disabled || tab.inert || tab.getAttribute("aria-disabled") === "true"
  }

  // Activate the currently focused tab
  activateFocusedTab() {
    const focusedTab = document.activeElement
    const index = this.tabTargets.indexOf(focusedTab)

    if (index === -1 || index === this.indexValue || this.isDisabled(focusedTab)) return

    // Dispatch cancelable before-switch event
    const beforeSwitchEvent = this.dispatch("before-switch", {
      detail: { fromIndex: this.indexValue, toIndex: index },
      cancelable: true
    })

    if (beforeSwitchEvent.defaultPrevented) {
      return
    }

    this.switchToTab(index)
  }

  // Public method to switch tabs programmatically
  switchToTab(index, dispatchEvent = true) {
    this.indexValue = index
    this.showTab(index, dispatchEvent)
    this.saveIndex(index)
  }

  // Get current tab index
  getCurrentIndex() {
    return this.indexValue
  }

  showTab(index, dispatchEvent = true, animate = true) {
    // Capture current height before switching (for animation)
    const container = this.hasPanelsContainerTarget ? this.panelsContainerTarget : null
    const startHeight = container ? container.offsetHeight : 0

    this.tabTargets.forEach((tab, i) => {
      const isActive = i === index

      if (isActive) {
        // Apply active classes
        if (this.hasActiveTabClass) {
          tab.classList.add(...this.activeTabClasses)
        }
        if (this.hasInactiveTabClass) {
          tab.classList.remove(...this.inactiveTabClasses)
        }
        tab.setAttribute("aria-selected", "true")
        tab.setAttribute("tabindex", "0")
      } else {
        // Apply inactive classes
        if (this.hasActiveTabClass) {
          tab.classList.remove(...this.activeTabClasses)
        }
        if (this.hasInactiveTabClass) {
          tab.classList.add(...this.inactiveTabClasses)
        }
        tab.setAttribute("aria-selected", "false")
        tab.setAttribute("tabindex", "-1")
      }
    })

    this.panelTargets.forEach((panel, i) => {
      const isHidden = i !== index

      if (this.hasHiddenPanelClass) {
        panel.classList.toggle(this.hiddenPanelClass, isHidden)
      } else {
        panel.classList.toggle("hidden", isHidden)
      }

      panel.setAttribute("aria-hidden", isHidden ? "true" : "false")
    })

    // Animate height transition
    if (container && animate && dispatchEvent) {
      this.animateHeight(container, startHeight)
    }

    // Update sliding indicator position
    if (this.hasIndicatorTarget) {
      this.positionIndicator(index, true)
    }

    if (dispatchEvent) {
      this.dispatch("switched", { detail: { index } })
    }
  }

  // Animate container height from startHeight to new content height
  animateHeight(container, startHeight) {
    // Get the new height after panel switch
    const endHeight = container.offsetHeight

    // Skip animation if heights are the same
    if (startHeight === endHeight) return

    // Set explicit start height
    container.style.height = `${startHeight}px`
    container.style.overflow = "hidden"

    // Force reflow
    container.offsetHeight

    // Animate to new height
    container.style.transition = "height 250ms cubic-bezier(0.4, 0, 0.2, 1)"
    container.style.height = `${endHeight}px`

    // Clean up after animation
    const cleanup = () => {
      container.style.height = ""
      container.style.overflow = ""
      container.style.transition = ""
      container.removeEventListener("transitionend", cleanup)
    }

    container.addEventListener("transitionend", cleanup)
  }

  // Position the sliding indicator behind the active tab
  positionIndicator(index, animate = true) {
    if (!this.hasIndicatorTarget) return

    const tab = this.tabTargets[index]
    if (!tab) return

    // Use offsetLeft/offsetWidth for position relative to parent
    const left = tab.offsetLeft
    const width = tab.offsetWidth

    // Apply or remove transition based on animate flag
    if (animate) {
      this.indicatorTarget.style.transition = "left 250ms cubic-bezier(0.4, 0, 0.2, 1), width 250ms cubic-bezier(0.4, 0, 0.2, 1)"
    } else {
      this.indicatorTarget.style.transition = "none"
    }

    this.indicatorTarget.style.left = `${left}px`
    this.indicatorTarget.style.width = `${width}px`
  }

  setupAriaAttributes() {
    // Generate unique IDs for tabs and panels
    const baseId = this.element.id || `tabs-${Math.random().toString(36).substr(2, 9)}`

    this.tabTargets.forEach((tab, i) => {
      const tabId = `${baseId}-tab-${i}`
      const panelId = `${baseId}-panel-${i}`

      tab.id = tabId
      tab.setAttribute("aria-controls", panelId)

      if (this.panelTargets[i]) {
        this.panelTargets[i].id = panelId
        this.panelTargets[i].setAttribute("aria-labelledby", tabId)
      }
    })
  }

  loadSavedIndex() {
    if (!this.storageKeyValue) {
      return null
    }

    try {
      const saved = localStorage.getItem(this.storageKeyValue)
      if (saved !== null) {
        const index = parseInt(saved, 10)
        // Validate the saved index is within bounds
        if (!isNaN(index) && index >= 0 && index < this.tabTargets.length) {
          return index
        }
      }
    } catch (e) {
      // localStorage not available or error reading
      console.warn("Could not read from localStorage:", e)
    }

    return null
  }

  saveIndex(index) {
    if (!this.storageKeyValue) {
      return
    }

    try {
      localStorage.setItem(this.storageKeyValue, index.toString())
    } catch (e) {
      // localStorage not available or error writing
      console.warn("Could not write to localStorage:", e)
    }
  }

  // Show tooltip near a tab
  showTooltip(message, tabIndex) {
    if (!this.hasTooltipTarget) return

    const tab = this.tabTargets[tabIndex]
    if (!tab) return

    this.tooltipTarget.textContent = message
    this.tooltipTarget.classList.remove("hidden")

    // Position tooltip below the tab
    const tabRect = tab.getBoundingClientRect()
    const containerRect = this.element.getBoundingClientRect()

    this.tooltipTarget.style.left = `${tabRect.left - containerRect.left + tabRect.width / 2}px`
    this.tooltipTarget.style.transform = "translateX(-50%)"

    // Auto-dismiss after 2 seconds
    setTimeout(() => {
      this.hideTooltip()
    }, 2000)
  }

  hideTooltip() {
    if (!this.hasTooltipTarget) return
    this.tooltipTarget.classList.add("hidden")
  }
}
